// src/hooks/useActivityFilters.js
import { useState, useCallback, useMemo } from "react";

/**
 * Custom hook for managing activity filters
 * Supports both professor and student portal filtering needs
 * @returns {Object} Filter state and handlers
 */
const useActivityFilters = () => {
  // Student-specific filters
  const [selectedType, setSelectedType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [attemptFilter, setAttemptFilter] = useState("");

  // Professor-specific filters
  const [student, setStudent] = useState("");
  const [department, setDepartment] = useState("");
  const [yearOfJoining, setYearOfJoining] = useState("");
  const [professorName, setProfessorName] = useState("");
  const [reactionType, setReactionType] = useState("");

  /**
   * Legacy filter object (for professor portal backward compatibility)
   */
  const filters = useMemo(
    () => ({
      student,
      department,
      type: selectedType,
      dateFrom,
      dateTo,
      yearOfJoining,
      professorName,
      reactionType,
    }),
    [
      student,
      department,
      selectedType,
      dateFrom,
      dateTo,
      yearOfJoining,
      professorName,
      reactionType,
    ],
  );

  /**
   * Update individual filter field (professor portal style)
   */
  const updateFilter = useCallback((field, value) => {
    switch (field) {
      case "student":
        setStudent(value);
        break;
      case "department":
        setDepartment(value);
        break;
      case "type":
        setSelectedType(value);
        break;
      case "dateFrom":
        setDateFrom(value);
        break;
      case "dateTo":
        setDateTo(value);
        break;
      case "yearOfJoining":
        setYearOfJoining(value);
        break;
      case "professorName":
        setProfessorName(value);
        break;
      case "reactionType":
        setReactionType(value);
        break;
      default:
        console.warn(`Unknown filter field: ${field}`);
    }
  }, []);

  /**
   * Current filters as API-ready object (student portal style)
   */
  const currentFilters = useMemo(() => {
    const apiFilters = {};

    if (selectedType) apiFilters.type = selectedType;
    if (dateFrom) apiFilters.date_from = dateFrom;
    if (dateTo) apiFilters.date_to = dateTo;
    if (statusFilter) apiFilters.status = statusFilter;
    if (attemptFilter) apiFilters.attempt_type = attemptFilter;
    if (student) apiFilters.student = student;
    if (department) apiFilters.department = department;
    if (yearOfJoining) apiFilters.year_of_joining = yearOfJoining;
    if (professorName) apiFilters.professor_name = professorName;
    if (reactionType) apiFilters.reaction_type = reactionType;

    return apiFilters;
  }, [
    selectedType,
    dateFrom,
    dateTo,
    statusFilter,
    attemptFilter,
    student,
    department,
    yearOfJoining,
    professorName,
    reactionType,
  ]);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return !!(
      selectedType ||
      dateFrom ||
      dateTo ||
      statusFilter ||
      attemptFilter ||
      student ||
      department ||
      yearOfJoining ||
      professorName ||
      reactionType
    );
  }, [
    selectedType,
    dateFrom,
    dateTo,
    statusFilter,
    attemptFilter,
    student,
    department,
    yearOfJoining,
    professorName,
    reactionType,
  ]);

  /**
   * Reset all filters
   */
  const resetFilters = useCallback(() => {
    // Student filters
    setSelectedType("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setAttemptFilter("");

    // Professor filters
    setStudent("");
    setDepartment("");
    setYearOfJoining("");
    setProfessorName("");
    setReactionType("");
  }, []);

  return {
    // Student portal style (modern)
    selectedType,
    setSelectedType,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    statusFilter,
    setStatusFilter,
    attemptFilter,
    setAttemptFilter,

    // Professor portal style (legacy)
    filters,
    updateFilter,

    // Shared utilities
    currentFilters,
    hasActiveFilters,
    resetFilters,
  };
};

export default useActivityFilters;

//v1.1
